import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, 
  Mail, 
  MessageSquare, 
  Phone, 
  Plus, 
  CalendarCheck, 
  Filter, 
  Check, 
  X,
  Download,
  FileText,
  Pencil,
  Trash,
  BarChart
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { CommunicationForm } from "@/components/communications/CommunicationForm";
import { ActionMenu } from "@/components/ui/action-menu";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  fetchCommunications,
  createCommunication,
  updateCommunication,
  deleteCommunication,
  createFollowUpCommunication
} from "@/services/communication/communicationService";
import { exportCommunicationsToCSV } from "@/services/communication/communicationExportService";
import { CommunicationWithRelations } from "@/types/communication.types";

export default function Communications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // States for forms and dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editCommunication, setEditCommunication] = useState<CommunicationWithRelations | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [communicationToDelete, setCommunicationToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Query for communications with optimistic updates
  const { 
    data: communications = [], 
    isLoading 
  } = useQuery({
    queryKey: ["communications", selectedTypes, selectedStatus],
    queryFn: () => fetchCommunications({
      types: selectedTypes.length ? selectedTypes : undefined,
      status: selectedStatus || undefined
    })
  });
  
  // Create mutation
  const createMutation = useMutation({
    mutationFn: createCommunication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communications"] });
      toast({
        title: "Success",
        description: "Communication recorded successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to record communication",
        variant: "destructive",
      });
    },
  });
  
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      updateCommunication(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communications"] });
      toast({
        title: "Success",
        description: "Communication updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update communication",
        variant: "destructive",
      });
    },
  });
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteCommunication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communications"] });
      toast({
        title: "Success",
        description: "Communication deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete communication",
        variant: "destructive",
      });
    },
  });
  
  // Follow-up mutation
  const followUpMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      createFollowUpCommunication(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communications"] });
      toast({
        title: "Success",
        description: "Follow-up communication created",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create follow-up",
        variant: "destructive",
      });
    },
  });
  
  // Handle form submission
  const handleFormSubmit = async (values: any) => {
    if (editCommunication) {
      await updateMutation.mutateAsync({
        id: editCommunication.id,
        updates: {
          ...values,
          user_id: user?.id
        }
      });
      setEditCommunication(null);
    } else {
      await createMutation.mutateAsync({
        ...values,
        user_id: user?.id
      });
    }
    setFormOpen(false);
  };
  
  // Handle edit click
  const handleEditClick = (communication: CommunicationWithRelations) => {
    setEditCommunication(communication);
    setFormOpen(true);
  };
  
  // Handle delete click
  const handleDeleteClick = (id: string) => {
    setCommunicationToDelete(id);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (communicationToDelete) {
      await deleteMutation.mutateAsync(communicationToDelete);
      setCommunicationToDelete(null);
      setDeleteDialogOpen(false);
    }
  };
  
  // Handle follow-up click
  const handleFollowUpClick = (communication: CommunicationWithRelations) => {
    // Create a follow-up with default values
    const followUpData = {
      type: communication.type,
      date: new Date(),
      subject: `Follow-up: ${communication.subject}`,
      user_id: user?.id
    };
    
    followUpMutation.mutate({
      id: communication.id,
      data: followUpData
    });
  };
  
  // Handle export click
  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportCommunicationsToCSV({
        types: selectedTypes.length ? selectedTypes : undefined,
        status: selectedStatus || undefined
      });
      toast({
        title: "Export Complete",
        description: "Communications exported to CSV successfully",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export communications",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  // Get icon for communication type
  const getIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "phone":
        return <Phone className="h-4 w-4" />;
      case "meeting":
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };
  
  // Filter communications by search term
  const filteredCommunications = communications.filter((communication) => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesSearch =
      communication.subject.toLowerCase().includes(searchTermLower) ||
      (communication.content?.toLowerCase().includes(searchTermLower) ?? false) ||
      (communication.organization?.name?.toLowerCase().includes(searchTermLower) ?? false) ||
      (`${communication.contact?.first_name} ${communication.contact?.last_name}`.toLowerCase().includes(searchTermLower) ?? false);
    
    return matchesSearch;
  });
  
  // Toggle communication type filter
  const toggleTypeFilter = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };
  
  // Toggle status filter
  const toggleStatusFilter = (status: string) => {
    if (selectedStatus === status) {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(status);
    }
  };
  
  // Group communications by date
  const groupedCommunications: Record<string, CommunicationWithRelations[]> = {};
  filteredCommunications.forEach((communication) => {
    const date = new Date(communication.date).toISOString().split("T")[0];
    if (!groupedCommunications[date]) {
      groupedCommunications[date] = [];
    }
    groupedCommunications[date].push(communication);
  });
  
  // Render date groups
  const renderDateGroups = () => {
    return Object.keys(groupedCommunications)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map((date) => (
        <div key={date} className="mb-8">
          <h2 className="text-sm font-medium text-muted-foreground mb-4">
            {format(new Date(date), "EEEE, MMMM d, yyyy")}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {groupedCommunications[date].map((communication) =>
              renderCommunicationCard(communication)
            )}
          </div>
        </div>
      ));
  };
  
  // Render communication card
  const renderCommunicationCard = (communication: CommunicationWithRelations) => {
    return (
      <Card key={communication.id} className="relative">
        <div className="absolute top-2 right-2">
          <ActionMenu
            actions={[
              {
                label: "Edit",
                onClick: () => handleEditClick(communication),
                icon: <Pencil className="h-4 w-4" />,
              },
              {
                label: "Delete",
                onClick: () => handleDeleteClick(communication.id),
                variant: "destructive",
                icon: <Trash className="h-4 w-4" />,
              },
              {
                label: "Create Follow-up",
                onClick: () => handleFollowUpClick(communication),
                icon: <CalendarCheck className="h-4 w-4" />,
              },
            ]}
          />
        </div>
        
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-primary/10 rounded-full">
                {getIcon(communication.type)}
              </div>
              <CardTitle className="text-base">{communication.subject}</CardTitle>
            </div>
          </div>
          <CardDescription className="flex justify-between pt-1">
            <span>
              {communication.contact?.first_name} {communication.contact?.last_name}
            </span>
            <span>
              {format(new Date(communication.date), "h:mm a")}
            </span>
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {communication.content || "No content provided."}
          </p>
          
          {communication.tags && communication.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1">
              {communication.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
        
        <CardFooter className="pt-0">
          <div className="flex items-center text-xs text-muted-foreground w-full justify-between">
            <span className="font-medium">
              {communication.organization?.name || "No organization"}
              {communication.opportunity && (
                <>
                  <span className="mx-1">•</span>
                  <span>{communication.opportunity.name}</span>
                </>
              )}
            </span>
            
            {communication.status && (
              <Badge 
                variant={
                  communication.status === "completed"
                    ? "default"
                    : communication.status === "pending"
                    ? "outline"
                    : "secondary"
                }
                className="ml-auto text-xs"
              >
                {communication.status}
              </Badge>
            )}
          </div>
        </CardFooter>
      </Card>
    );
  };
  
  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-120px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Communications</h1>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/communications/templates">
              <FileText className="mr-2 h-4 w-4" />
              Email Templates
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/communications/campaigns/analytics">
              <BarChart className="mr-2 h-4 w-4" />
              Analytics
            </Link>
          </Button>
          <Button 
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || filteredCommunications.length === 0}
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export
          </Button>
          <Button onClick={() => { setEditCommunication(null); setFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            New Communication
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Input
            placeholder="Search communications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filters
              {(selectedTypes.length > 0 || selectedStatus) && (
                <Badge variant="secondary" className="ml-2 rounded-full px-1">
                  {selectedTypes.length + (selectedStatus ? 1 : 0)}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Type</h4>
                <div className="flex flex-wrap gap-2">
                  {["email", "phone", "meeting"].map((type) => (
                    <Button
                      key={type}
                      size="sm"
                      variant={selectedTypes.includes(type) ? "default" : "outline"}
                      onClick={() => toggleTypeFilter(type)}
                      className="flex items-center gap-1"
                    >
                      {getIcon(type)}
                      <span className="capitalize">{type}</span>
                      {selectedTypes.includes(type) && (
                        <Check className="h-3 w-3 ml-1" />
                      )}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2">Status</h4>
                <div className="flex flex-wrap gap-2">
                  {["completed", "pending", "requires_followup"].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedStatus === status ? "default" : "outline"}
                      onClick={() => toggleStatusFilter(status)}
                      className="flex items-center gap-1"
                    >
                      <span className="capitalize">{status.replace("_", " ")}</span>
                      {selectedStatus === status && (
                        <Check className="h-3 w-3 ml-1" />
                      )}
                    </Button>
                  ))}
                </div>
              </div>
              
              {(selectedTypes.length > 0 || selectedStatus) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedTypes([]);
                    setSelectedStatus(null);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Communications</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="received">Received</TabsTrigger>
          <TabsTrigger value="follow-ups">Follow-ups</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all">
          {filteredCommunications.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">
                  No communications found. Create your first communication by clicking the "New Communication" button.
                </p>
              </CardContent>
            </Card>
          ) : (
            renderDateGroups()
          )}
        </TabsContent>
        
        <TabsContent value="sent">
          {filteredCommunications.filter(c => c.is_outbound).length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No sent communications found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredCommunications
                .filter(c => c.is_outbound)
                .map(renderCommunicationCard)}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="received">
          {filteredCommunications.filter(c => !c.is_outbound).length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No received communications found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredCommunications
                .filter(c => !c.is_outbound)
                .map(renderCommunicationCard)}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="follow-ups">
          {filteredCommunications.filter(c => c.status === 'requires_followup').length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">No communications requiring follow-up.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredCommunications
                .filter(c => c.status === 'requires_followup')
                .map(renderCommunicationCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Communication Form Dialog */}
      <CommunicationForm
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditCommunication(null);
        }}
        onSubmit={handleFormSubmit}
        initialValues={editCommunication || undefined}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Communication"
        description="Are you sure you want to delete this communication? This action cannot be undone."
      />
    </div>
  );
}